import useSWR from "swr";
import { CONFIG } from "@/config-global";
import axios, { fetcher, endpoints } from "@/utils/axios";

// ----------------------------------------------------------------------
const WORKSPACE_ENDPOINT = `${CONFIG.serverUrl}${endpoints.workspace}`;

const ENDPOINTS = {
  GET_OWNED_WORKSPACE_ID: "get-owned-workspace-id",
  GET_WORKSPACE_MEMBERS: "get-workspace-members",
  GET_WORKSPACE_SETTINGS: "get-workspace-settings",
  UPDATE_WORKSPACE_SETTINGS: "update-workspace-settings",
};

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

/**
 * Hook to fetch the user's owned workspace ID
 */
export function useOwnedWorkspaceId(userId: string | null) {
  const url = userId
    ? [
        `${WORKSPACE_ENDPOINT}`,
        { params: { endpoint: ENDPOINTS.GET_OWNED_WORKSPACE_ID } },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    url,
    fetcher,
    swrOptions
  );

  return {
    workspaceId: data?.workspaceId,
    isLoading,
    error,
    isValidating,
    refresh: mutate,
  };
}

// ----------------------------------------------------------------------

/**
 * Function to directly fetch the user's owned workspace ID
 */
export async function getOwnedWorkspaceId() {
  try {
    const params = { endpoint: ENDPOINTS.GET_OWNED_WORKSPACE_ID };
    const response = await axios.get(WORKSPACE_ENDPOINT, { params });
    return response.data.workspaceId;
  } catch (error) {
    console.error("Error fetching owned workspace ID:", error);
    throw error;
  }
}

// ----------------------------------------------------------------------

/**
 * Hook to fetch workspace members
 */
export function useWorkspaceMembers(workspaceId: string | null) {
  const url = workspaceId
    ? [
        `${WORKSPACE_ENDPOINT}`,
        { params: { endpoint: ENDPOINTS.GET_WORKSPACE_MEMBERS, workspaceId } },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    url,
    fetcher,
    swrOptions
  );

  return {
    members: data?.members || [],
    isLoading,
    error,
    isValidating,
    refresh: mutate,
  };
}

// ----------------------------------------------------------------------

/**
 * Function to directly fetch workspace members
 */
export async function getWorkspaceMembers(workspaceId: string) {
  try {
    const params = {
      endpoint: ENDPOINTS.GET_WORKSPACE_MEMBERS,
      workspaceId,
    };
    const response = await axios.get(WORKSPACE_ENDPOINT, { params });
    return response.data.members;
  } catch (error) {
    console.error("Error fetching workspace members:", error);
    throw error;
  }
}

// ----------------------------------------------------------------------

/**
 * Hook to fetch workspace settings
 */
export function useWorkspaceSettings(workspaceId: string | null) {
  const url = workspaceId
    ? [
        `${WORKSPACE_ENDPOINT}`,
        { params: { endpoint: ENDPOINTS.GET_WORKSPACE_SETTINGS, workspaceId } },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    url,
    fetcher,
    swrOptions
  );

  return {
    workspace: data?.workspace,
    isLoading,
    error,
    isValidating,
    refresh: mutate,
  };
}

// ----------------------------------------------------------------------

/**
 * Function to update workspace settings
 */
export async function updateWorkspaceSettings(
  workspaceId: string,
  settingsData: {
    clientPortalSettings?: {
      welcomeMessage?: string;
      showChat?: boolean;
      showTimeline?: boolean;
      showFiles?: boolean;
      showMeetingSummaries?: boolean;
      allowChangeRequests?: boolean;
    };
  }
) {
  const params = { endpoint: ENDPOINTS.UPDATE_WORKSPACE_SETTINGS, workspaceId };
  const url = `${WORKSPACE_ENDPOINT}`;

  const response = await axios.patch(url, settingsData, { params });
  return response.data;
}
